import { jwtDecode, JwtPayload } from "jwt-decode";
import { z } from "zod";

const TokenDataSchema = z.object({
  email: z.string(),
  expirationTime: z.date(),
});

export type TokenData = z.infer<typeof TokenDataSchema>;

type CognitoJwtPayload = JwtPayload & {
  email: string;
};

/**
 * Decode the ID JWT and return the token data.
 */
const decodeIdToken = (idToken: string): TokenData => {
  const jwtPayload = jwtDecode<CognitoJwtPayload>(idToken);
  return TokenDataSchema.parse({
    email: jwtPayload.email,
    expirationTime: new Date(jwtPayload.exp! * 1000),
  });
};

export class AuthTokens {
  private idToken: string = "";
  private accessToken: string = "";
  private refreshToken: string = "";
  private tokenData: TokenData | null = null;

  constructor(idToken: string, accessToken: string, refreshToken: string) {
    if (!idToken || !accessToken || !refreshToken) {
      console.warn("Not all tokens were provided");
      return;
    }

    this.idToken = idToken;
    this.accessToken = accessToken;
    this.refreshToken = refreshToken;

    this.tokenData = decodeIdToken(idToken);
  }

  static fromStorage(): AuthTokens {
    const idToken = localStorage.getItem("idToken") || "";
    const accessToken = localStorage.getItem("accessToken") || "";
    const refreshToken = localStorage.getItem("refreshToken") || "";

    return new AuthTokens(idToken, accessToken, refreshToken);
  }

  get IdToken() {
    return this.idToken;
  }

  get AccessToken() {
    return this.accessToken;
  }

  get RefreshToken() {
    return this.refreshToken;
  }

  get TokenData() {
    return this.tokenData;
  }

  store() {
    localStorage.setItem("idToken", this.idToken || "");
    localStorage.setItem("accessToken", this.accessToken || "");
    localStorage.setItem("refreshToken", this.refreshToken || "");
  }

  static async remove() {
    localStorage.removeItem("idToken");
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");

    await new Promise((resolve) => setTimeout(resolve, 1000));
  }

  isExpired(): boolean {
    if (!this.idToken) {
      return true;
    }

    const tokenExpiration = this.tokenData?.expirationTime.getTime() || 0;

    return Date.now() >= tokenExpiration;
  }
}
